import React from 'react'
import { Box, BoxProps, Slider, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'

export interface SliderControlProps extends Omit<BoxProps, 'onChange' | 'children'> {
  label: string
  value: number
  min?: number
  max?: number
  step?: number
  onChange: (value: number) => void
  /** Format the value shown next to the slider. Defaults to rounding. */
  valueFormatter?: (value: number) => string
  disabled?: boolean
}

const Head = styled(Box)({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  marginBottom: 6,
})

const Label = styled(Typography)({
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--ds-ink-muted)',
})

const Value = styled(Typography)({
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--ds-ink-muted)',
  fontVariantNumeric: 'tabular-nums',
})

const StyledSlider = styled(Slider)({
  color: 'var(--ds-ink)',
  height: 4,
  padding: '10px 0',
  '& .MuiSlider-thumb': {
    width: 16,
    height: 16,
    backgroundColor: 'var(--ds-surface)',
    border: '2px solid var(--ds-ink)',
    boxShadow: 'var(--ds-shadow-soft)',
    '&:hover, &.Mui-focusVisible': {
      boxShadow: '0 0 0 6px rgba(17,17,17,0.08)',
    },
    '&.Mui-active': {
      boxShadow: '0 0 0 8px rgba(17,17,17,0.1)',
    },
  },
  '& .MuiSlider-rail': {
    opacity: 1,
    backgroundColor: 'var(--ds-border)',
  },
  '& .MuiSlider-track': {
    border: 'none',
  },
  '&.Mui-disabled': {
    opacity: 0.5,
  },
})

/** Labelled, token-styled slider — matting width, zoom, camera angle, etc. */
export function SliderControl({
  label,
  value,
  min = 0,
  max = 100,
  step = 1,
  onChange,
  valueFormatter,
  disabled,
  ...rest
}: SliderControlProps) {
  return (
    <Box {...rest}>
      <Head>
        <Label>{label}</Label>
        <Value>{valueFormatter ? valueFormatter(value) : Math.round(value)}</Value>
      </Head>
      <StyledSlider
        value={value}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        onChange={(_, v) => onChange(Array.isArray(v) ? v[0] : v)}
        aria-label={label}
      />
    </Box>
  )
}
